import React from 'react';

interface Props {
  /** Sample company row from /api/ai-search/sample-entity. */
  company: any | null;
  /** Columns currently included in the active filter group. */
  selectedColumns: Set<string>;
  /** Toggles ALL columns of a clicked cell in the active filter group. */
  onToggleColumns: (columns: string[]) => void;
}

const truncate = (s: string | null | undefined, n: number): string => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s;
};

const formatColumnLabel = (col: string): string =>
  col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatArray = (v: any): string => {
  if (!v) return '';
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  if (typeof v === 'string' && v.startsWith('{')) return v.replace(/[{}]/g, '').split(',').join(', ');
  if (typeof v === 'string' && v.startsWith('[')) {
    try { return JSON.parse(v).filter(Boolean).join(', '); } catch { return v; }
  }
  return String(v);
};

// Same wrapper pattern as CandidatePickerSchematic — rendered inline so
// the file is self-contained and the styling stays in sync.
const PickableCell: React.FC<{
  columns: string[];
  selectedColumns: Set<string>;
  onToggleColumns: (cols: string[]) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ columns, selectedColumns, onToggleColumns, children, className }) => {
  const allSelected = columns.length > 0 && columns.every(c => selectedColumns.has(c));
  const someSelected = !allSelected && columns.some(c => selectedColumns.has(c));

  const stateClass = allSelected
    ? 'border-amber-400 bg-amber-50/70 ring-1 ring-amber-200'
    : someSelected
      ? 'border-amber-300 bg-amber-50/40'
      : 'border-transparent hover:border-blue-300 hover:bg-blue-50/40';

  const label = columns.map(formatColumnLabel).join(' · ');

  return (
    <button
      type="button"
      onClick={() => onToggleColumns(columns)}
      className={`group relative w-full text-left rounded-lg border-2 transition-colors p-2 ${stateClass} ${className || ''}`}
      title={`${allSelected ? 'Click to remove' : 'Click to add'}: ${columns.join(', ')}`}
      aria-pressed={allSelected}
    >
      <span
        className={`absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition-opacity pointer-events-none ${
          allSelected
            ? 'bg-amber-200 text-amber-900 opacity-100'
            : someSelected
              ? 'bg-amber-100 text-amber-800 opacity-100'
              : 'bg-gray-700 text-white opacity-0 group-hover:opacity-90'
        }`}
      >
        {label}
      </span>
      {children}
    </button>
  );
};

const Empty: React.FC = () => <span className="text-gray-400 italic">—</span>;

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-2">{children}</h4>
);

const CompanyPickerSchematic: React.FC<Props> = ({ company, selectedColumns, onToggleColumns }) => {
  if (!company) {
    return (
      <p className="text-xs text-gray-500 italic">
        No sample company available — add companies to the project to see the visual picker.
      </p>
    );
  }

  const co = company;
  const sectionProps = { selectedColumns, onToggleColumns };

  return (
    <div className="space-y-3">
      {/* Header — name, industry, headquarters */}
      <div>
        <PickableCell columns={['company_name']} {...sectionProps}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {(co.company_name?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{co.company_name || <Empty />}</p>
            </div>
          </div>
        </PickableCell>

        <div className="grid grid-cols-2 gap-1 mt-1">
          <PickableCell columns={['industry']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Industry</p>
            <p className="text-xs text-gray-700 truncate">{co.industry || <Empty />}</p>
          </PickableCell>
          <PickableCell columns={['subindustry']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Sub-industry</p>
            <p className="text-xs text-gray-700 truncate">{formatArray(co.subindustry) || <Empty />}</p>
          </PickableCell>
        </div>

        <PickableCell columns={['formatted_address', 'office_address', 'headquarters']} {...sectionProps} className="mt-1">
          <p className="text-xs text-gray-600">
            {co.formatted_address || co.office_address || co.headquarters || <Empty />}
          </p>
        </PickableCell>
      </div>

      {/* Company details */}
      <div>
        <SectionHeader>Company details</SectionHeader>
        <div className="space-y-1">
          <PickableCell columns={['business_type']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Business type</p>
            <p className="text-xs text-gray-700">{formatArray(co.business_type) || <Empty />}</p>
          </PickableCell>

          <PickableCell columns={['pattern_tags']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Tags</p>
            <p className="text-xs text-gray-700">{formatArray(co.pattern_tags) || <Empty />}</p>
          </PickableCell>

          <PickableCell columns={['primary_offerings']} {...sectionProps}>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Primary offerings</p>
            <p className="text-xs text-gray-700">{formatArray(co.primary_offerings) || <Empty />}</p>
          </PickableCell>

          <div className="grid grid-cols-2 gap-1">
            <PickableCell columns={['website']} {...sectionProps}>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Website</p>
              <p className="text-xs text-gray-700 truncate">{co.website || <Empty />}</p>
            </PickableCell>
            <PickableCell columns={['company_domain']} {...sectionProps}>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Domain</p>
              <p className="text-xs text-gray-700 truncate">{co.company_domain || <Empty />}</p>
            </PickableCell>
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <SectionHeader>Description</SectionHeader>
        <PickableCell columns={['description']} {...sectionProps}>
          <p className="text-xs text-gray-700 whitespace-pre-line">
            {co.description ? truncate(co.description, 320) : <Empty />}
          </p>
        </PickableCell>
      </div>

      {/* Specialties */}
      <div>
        <SectionHeader>Specialties</SectionHeader>
        <PickableCell columns={['specialties']} {...sectionProps}>
          <p className="text-xs text-gray-700">
            {co.specialties ? truncate(formatArray(co.specialties), 240) : <Empty />}
          </p>
        </PickableCell>
      </div>
    </div>
  );
};

export const COMPANY_PICKER_COLUMNS: string[] = [
  'company_name',
  'industry', 'subindustry',
  'formatted_address', 'office_address', 'headquarters',
  'business_type', 'pattern_tags', 'primary_offerings',
  'website', 'company_domain',
  'description', 'specialties'
];

export default CompanyPickerSchematic;
